import {useEffect, useRef, useState} from 'react';
import {useAtomValue, useSetAtom} from 'jotai';
import {useApolloClient} from '@apollo/client';
import {isOnlineAtom, authStatusAtom, checkAuthReachable} from './authAtoms';
import {
   offlineQueueAtom,
   offlineQueueHydratedAtom,
   syncStateAtom,
   applySyncResultAtom,
   MUTATION_REGISTRY,
   type QueuedMutation,
} from './offlineQueue';
import {syncAllPendingRunTicketPhotosAtom} from './runTicketPhotoSync';
import {withTimeout} from '@/lib/withTimeout';
import {mutationFailure} from '@/lib/mutationErrors';

// FIXED 2026-08-27 (Duane): "I went into internet and it started
// syncing, but lost internet and now it is 'syncing' forever." A
// mutation's `await client.mutate(...)` below has no timeout of its
// own — if connectivity drops mid-request (as opposed to being offline
// from the start, which fails fast), the underlying fetch can simply
// stall with no error and no response, for as long as the OS/browser's
// own TCP-level timeout takes (which can be minutes, or effectively
// forever on some networks). Nothing in that case ever reaches the
// `syncingRef.current = false` line below, so the sync effect's own
// guard (`|| syncingRef.current`) permanently blocks every future
// reconnect from ever retrying — exactly the stuck spinner reported.
// Racing each mutate call against this timeout (same `withTimeout`
// utility lib/apolloClient.ts already uses for an analogous
// can-hang-forever IndexedDB dependency) guarantees the loop always
// reaches its cleanup, one way or another, within a bounded time.
const MUTATION_SYNC_TIMEOUT_MS = 15000;

// FIXED 2026-08-28 (Duane): "the 1 Pending is still waiting to sync
// even though the network is back for more than 2 minutes." The effect
// below only re-runs when `isOnline` or `hydrated` actually CHANGE
// (its own dependency array) — it does NOT re-run just because `queue`
// grew. That's fine for the textbook case (genuinely offline, submit
// queues it, device comes back online, isOnline flips false→true,
// effect re-fires) but misses a case this whole offline QA pass kept
// running into: `isOnlineAtom` reading `true` the entire time because
// the connection LOOKED up (navigator.onLine never flipped), while the
// actual request still failed/timed out and useOfflineMutation.ts's
// submit() fell back to queuing it anyway. In that case `isOnline`
// never transitions — it was true before, true after — so this effect
// has no signal that there's now new work to retry, and the item just
// sits there until some unrelated isOnline/hydrated change happens to
// come along.
//
// Also a second-line backstop for the same class of gap
// useAuthBootstrap.ts's visibilitychange/focus recheck (added the same
// day) covers for `isOnlineAtom` itself: no single event-driven signal
// has proven reliable enough across real mobile browsers this session.
// Rather than chase yet another missed event, this just polls: every
// SYNC_POLL_INTERVAL_MS, bump a tick counter that's in the effect's
// dependency array, so it re-evaluates its own guard
// (`!isOnline || !hydrated || queue.length === 0 || syncingRef.current`)
// on a fixed cadence regardless of why it didn't fire on its own. The
// guard makes every "nothing to do" tick effectively free (a handful of
// atom reads, no network activity), so this costs nothing while the
// queue is empty — which is the overwhelming majority of the time for
// any given user.
const SYNC_POLL_INTERVAL_MS = 10000;

/**
 * Flushes the offline mutation queue whenever the device transitions
 * back online, once the queue finishes hydrating from IndexedDB if the
 * device is already online at that point (e.g. app was closed offline
 * and reopened with signal), or on the next SYNC_POLL_INTERVAL_MS poll
 * tick if neither of those fired but there's queued work and the device
 * currently looks online anyway (see SYNC_POLL_INTERVAL_MS's own
 * comment — a real-world backstop for reconnects the event-driven path
 * misses).
 *
 * Mutations replay in the order they were queued. A mutation that
 * fails for a reason other than "still offline" (e.g. a real
 * validation error from the server) is left in the queue with an
 * incremented retryCount and lastError so the sync UI can surface it
 * for the user to resolve — it is NOT silently dropped.
 */
// ADDED 2026-08-31 (Duane): "the number of outdated reports on the well
// page didn't update to the actual number and the status reverted to
// before going offline. Sometimes it does update and sometimes it
// doesn't." Two distinct problems in the 2026-08-29 refetchQueries fix
// below (see its own doc comment for the original bug): (1)
// applySyncResult — which removes a just-succeeded entry from
// offlineQueueAtom, taking its offline-queue overlay down with it — used
// to run BEFORE the refetch, so there was always at least one render
// where the overlay had already disappeared but the real query result
// hadn't caught up yet, a genuine revert-then-recover flash that reads
// as "never recovered" if the refetch is at all slow. (2) refetchQueries
// itself was a single best-effort attempt with no retry, right at
// reconnect — the single flakiest moment for a field connection to have
// a request fail — and a failed refetch here had no way to ever
// self-correct: once the queue is empty, this effect's own guard
// (`queue.length === 0`) skips the syncing branch entirely on every
// future run, poll tick included, so nothing would ever retry it short
// of an unrelated remount. Fixed by making the refetch itself retry a
// few times with a short gap (the next attempt a couple seconds later is
// often enough once a just-reconnected link settles), and reordering the
// caller below so this finishes BEFORE applySyncResult runs — see that
// call site's own comment.
const REFETCH_RETRY_ATTEMPTS = 3;
const REFETCH_RETRY_DELAY_MS = 2000;

async function refetchActiveQueriesWithRetry(client: ReturnType<typeof useApolloClient>) {
   for (let attempt = 1; attempt <= REFETCH_RETRY_ATTEMPTS; attempt++) {
      try {
         await client.refetchQueries({include: 'active'});
         return;
      } catch (err) {
         if (attempt === REFETCH_RETRY_ATTEMPTS) {
            console.warn(`refetchActiveQueriesWithRetry: giving up after ${attempt} attempts`, err);
            return;
         }
         await new Promise((resolve) => setTimeout(resolve, REFETCH_RETRY_DELAY_MS));
      }
   }
}

// ADDED 2026-09-04 (Duane): "make sure there is actual auth reachability
// before sending the whole queue. If there isn't it needs to back off a
// bit before trying again. If there still isn't it needs to back off
// more." Root cause this closes: this hook's only gate on firing is
// `isOnline` (see the effect's guard below), which is driven purely by
// lib/onlineState.ts's probeReachability — a HEAD request to a static
// asset on the APP's own origin, not to Cognito. The two are different
// AWS services and can disagree right at a real-world reconnect (the
// app's own CDN/static hosting can come back a beat before Cognito's
// regional endpoint does) — and the old behavior was to replay the
// ENTIRE queued backlog the instant `isOnline` flipped true, with no
// check that a token was actually obtainable first. Every queued
// mutation in that pass would independently hit authLink's
// fetchAuthSession() catch (see apolloClient.ts) and go out with no
// accesstoken header — a guaranteed 401 per entry, not a maybe. Worse,
// nothing about that failure looks like "still offline" to this hook
// (client.mutate's own rejection isn't network-shaped — it's a real
// HTTP 401 response), so SYNC_POLL_INTERVAL_MS would retry the whole
// backlog again 10 seconds later, and again, for as long as the
// connection sat in that in-between state — the exact "AWS logs show a
// lot of 401 errors" pattern this was written to fix.
//
// Fixed by checking Cognito reachability (checkAuthReachable, which
// reuses the same fetchAuthSession() call authLink itself relies on —
// see that function's own comment in authAtoms.ts) BEFORE ever touching
// the mutation queue, and backing off exponentially (doubling each
// consecutive failure, capped at AUTH_BACKOFF_MAX_MS) when it isn't
// reachable yet, so a prolonged in-between state degrades to one check
// every few minutes instead of hammering Cognito — and generating a
// fresh burst of guaranteed 401s — on every single poll tick.
const AUTH_CHECK_TIMEOUT_MS = 8000;
const AUTH_BACKOFF_BASE_MS = 10000;
const AUTH_BACKOFF_MULTIPLIER = 2;
const AUTH_BACKOFF_MAX_MS = 5 * 60 * 1000; // 5 minutes

export function useOfflineSync() {
   const isOnline = useAtomValue(isOnlineAtom);
   // FIXED 2026-08-31 (same class of gap as
   // unauthenticated_gql_requests_reconciliation_hook_2026_08_29.md):
   // this hook is mounted globally in App.tsx, outside RequireAuth, same
   // as useRunTicketReconciliation was — nothing previously stopped it
   // from replaying queued mutations (or useOfflinePhotoSync's sibling
   // effect from attempting an upload/attach) with no access token if
   // genuinely-queued offline work survived past a sign-out. Same
   // allow-list RequireAuth.tsx/useRunTicketReconciliation.ts use.
   const authStatus = useAtomValue(authStatusAtom);
   const isAuthed = authStatus === 'authenticated' || authStatus === 'offline-cached';
   const hydrated = useAtomValue(offlineQueueHydratedAtom);
   const queue = useAtomValue(offlineQueueAtom);
   const applySyncResult = useSetAtom(applySyncResultAtom);
   const setSyncState = useSetAtom(syncStateAtom);
   // A RunTicket that was just created by this flush (e.g. Submit Report
   // was tapped while offline, queued, and only now synced on reconnect)
   // may have a pending photo waiting on it — see runTicketPhotoSync.ts's
   // own doc comment on why that attach step can never create the
   // RunTicket itself. Running this right after a successful flush picks
   // it up in the same pass instead of waiting on a second reconnect.
   const syncAllPendingRunTicketPhotos = useSetAtom(syncAllPendingRunTicketPhotosAtom);
   const client = useApolloClient();
   const syncingRef = useRef(false);
   // See SYNC_POLL_INTERVAL_MS's own comment above — this only exists to
   // put a changing value in the main effect's dependency array on a
   // fixed cadence; the tick's number is never read for anything else.
   const [pollTick, setPollTick] = useState(0);
   // FIXED 2026-08-28 (Duane), alongside the poll above: this used to be
   // a plain `let cancelled = false` local to each effect invocation,
   // flipped by that invocation's own cleanup function. That's the
   // standard React pattern for "abandon this effect's async work if it
   // re-runs before finishing" — but it actively broke once pollTick
   // started causing re-runs every SYNC_POLL_INTERVAL_MS: a poll tick
   // firing while a sync pass was still mid-flight (e.g. waiting out
   // MUTATION_SYNC_TIMEOUT_MS on a hung mutate call) triggered the
   // PREVIOUS invocation's cleanup — flipping ITS `cancelled` to true —
   // even though the poll-triggered re-run was a same-online no-op
   // (syncingRef.current still true, so it returns immediately below
   // without starting anything new). The original in-flight pass would
   // then finish, see `cancelled === true`, and skip applying its own
   // result/setSyncState entirely — the exact "still says pending/
   // syncing forever" symptom this poll was added to fix, just moved
   // one layer down. A ref that's only ever set true by a genuine
   // component unmount (see the effect right below) doesn't have this
   // problem: it's unaffected by how many times the main effect below
   // re-runs, and still protects against setting state after this hook
   // is actually gone.
   const unmountedRef = useRef(false);
   // See AUTH_BACKOFF_BASE_MS's own comment above — tracks consecutive
   // "Cognito wasn't reachable" checks so retries back off exponentially
   // instead of re-attempting the whole queue on every poll tick. Plain
   // ref, not atom — same reasoning as syncingRef/unmountedRef: nothing
   // needs to reactively display this.
   const authBackoffRef = useRef({failureCount: 0, nextAttemptAt: 0});

   useEffect(() => {
      const interval = setInterval(() => setPollTick((t) => t + 1), SYNC_POLL_INTERVAL_MS);
      return () => clearInterval(interval);
   }, []);

   // BUG FIX 2026-08-29 (found while re-verifying the sync-icon fix
   // below): this effect's cleanup sets unmountedRef.current = true but
   // nothing ever set it back to false, so React 18 StrictMode's
   // dev-only double-invoke of effects on mount (mount → cleanup →
   // mount again, to surface exactly this class of bug) permanently
   // stuck it at true from the very first render — before the main
   // sync effect below ever got a chance to run a real pass. Every
   // subsequent sync attempt then hit `if (unmountedRef.current) break;`
   // on its very first loop iteration and returned without ever calling
   // setSyncState past 'syncing' or touching the queue, which is exactly
   // the "stuck on syncing forever, even reconnected, even after a
   // reload" symptom this was confused for a display bug at first:
   // confirmed live by reading IndexedDB directly (lib/offlineDb.ts's
   // pending-mutations store) and finding retryCount still 0 and the
   // entries still present tens of seconds past MUTATION_SYNC_TIMEOUT_MS,
   // meaning the sync loop never even reached its first client.mutate
   // call, rather than a mutate call actually hanging. Resetting to
   // false on every (re)mount is the standard fix for this ref pattern
   // under StrictMode — cleanup still marks a genuine unmount, but a
   // remount (real or StrictMode-simulated) no longer inherits a stale
   // true from the previous cleanup.
   useEffect(() => {
      unmountedRef.current = false;
      return () => {
         unmountedRef.current = true;
      };
   }, []);

   useEffect(() => {
      if (!isAuthed || !isOnline || !hydrated || queue.length === 0 || syncingRef.current) return;
      // See AUTH_BACKOFF_BASE_MS's own comment above — skip this tick
      // entirely (cheap: no network activity) while backing off from a
      // recently failed reachability check. The next poll tick (or a
      // fresh isOnline/hydrated transition) re-evaluates this on its
      // own; no separate timer needed.
      if (Date.now() < authBackoffRef.current.nextAttemptAt) return;

      // Flipped synchronously, same as before this change — an
      // observer (the UI's isSyncing check, or a test's own waitFor)
      // must see 'syncing' the instant this effect decides to act, not
      // after the auth-reachability await below settles (which would
      // leave a real window where this atom still reads its default
      // 'idle' value even though a pass is genuinely in flight).
      syncingRef.current = true;
      setSyncState('syncing');

      (async () => {
         // Confirm a token is actually obtainable BEFORE touching the
         // queue — see AUTH_BACKOFF_BASE_MS's own comment above for the
         // failure mode this prevents. Raced against a timeout for the
         // same reason every other can-hang dependency in this file is
         // (see MUTATION_SYNC_TIMEOUT_MS's own comment): a
         // fetchAuthSession() call that never settles must not
         // permanently block syncingRef/this hook's future retries.
         const authReachable = await withTimeout(checkAuthReachable(), AUTH_CHECK_TIMEOUT_MS, () => {
            console.warn(
               `checkAuthReachable() hadn't finished after ${AUTH_CHECK_TIMEOUT_MS}ms — treating as unreachable this pass`,
            );
         });
         if (unmountedRef.current) {
            syncingRef.current = false;
            return;
         }
         if (!authReachable) {
            const {failureCount} = authBackoffRef.current;
            const delay = Math.min(AUTH_BACKOFF_BASE_MS * AUTH_BACKOFF_MULTIPLIER ** failureCount, AUTH_BACKOFF_MAX_MS);
            authBackoffRef.current = {failureCount: failureCount + 1, nextAttemptAt: Date.now() + delay};
            // Nothing was actually attempted this pass — 'idle' (not
            // 'error') is the accurate state: this isn't a failure that
            // needs the user's attention, it's a routine retry that
            // will happen on its own once Cognito is reachable again.
            setSyncState('idle');
            syncingRef.current = false;
            return;
         }
         authBackoffRef.current = {failureCount: 0, nextAttemptAt: 0};

         const remaining = [...queue];
         const succeededEntityKeys: string[] = [];
         const failures: QueuedMutation[] = [];

         for (let i = 0; i < remaining.length; i++) {
            if (unmountedRef.current) break;
            const entry = remaining[i];
            const mutation = MUTATION_REGISTRY[entry.mutationKey];
            if (!mutation) {
               // Unknown mutation key (e.g. shipped an app update that
               // renamed a mutation) — drop it rather than looping
               // forever, treating it as "succeeded" for cleanup purposes.
               succeededEntityKeys.push(entry.entityKey);
               continue;
            }
            let timedOut = false;
            // FIXED 2026-08-28 (Duane): IntelliJ's "'throw' of exception
            // caught locally" inspection flagged the two throws that used
            // to live in the try block below (the !result / !result.data
            // cases) — both were guaranteed to be caught by the catch
            // right underneath them in this same function, which is
            // exactly the anti-pattern that inspection exists to catch:
            // using throw/catch as a same-scope goto instead of just
            // computing the value directly. Restructured so `err` is a
            // plain local the try block can also just assign to, and the
            // catch below is left doing only what a catch should — handle
            // an exception actually thrown by something else (client.mutate
            // / withTimeout's own internals, e.g. a real network rejection).
            let err: unknown;
            try {
               // errorPolicy: 'all' + data-presence check — same fix, same
               // reason, as useOfflineMutation.ts's own submit(). A queued
               // mutation that actually succeeds server-side but comes back
               // with a cosmetic accompanying error would otherwise be thrown
               // away here too, landing this entry right back in `failures`
               // for the user to see as an unresolved sync error even though
               // nothing needs retrying.
               //
               // Raced against MUTATION_SYNC_TIMEOUT_MS (see its own comment
               // above) — withTimeout resolves `undefined` rather than
               // rejecting when the timeout wins, and does NOT cancel the
               // underlying request (there's no way to cancel a native
               // Promise); a mutate call that times out here and later
               // resolves in the background is simply ignored. Retrying the
               // same variables again once reconnected is safe either way —
               // every *_CreateUpdate mutation in this app is an id-keyed
               // upsert (see graphql/custom/README.md), so replaying one that
               // actually did go through server-side just overwrites it with
               // the same values again, not a duplicate row.
               const result = await withTimeout(
                  client.mutate({mutation, variables: entry.variables, errorPolicy: 'all'}),
                  MUTATION_SYNC_TIMEOUT_MS,
                  () => {
                     timedOut = true;
                  },
               );
               if (!result) {
                  err = new Error('Sync timed out — will retry when reconnected');
               } else {
                  // Same rejected-write detection as useOfflineMutation: a
                  // null root field plus errors is a refusal, not a success
                  // (see lib/mutationErrors.ts). It stays queued with its
                  // lastError instead of being silently cleared.
                  err = mutationFailure(result.data, result.errors, entry.label) ?? undefined;
                  if (!err) succeededEntityKeys.push(entry.entityKey);
               }
            } catch (caughtErr) {
               err = caughtErr;
            }
            if (err) {
               failures.push({
                  ...entry,
                  retryCount: entry.retryCount + 1,
                  lastError: err instanceof Error ? err.message : 'Sync failed',
               });
               // A timeout almost always means connectivity dropped again
               // mid-pass (as opposed to a real server-side rejection, which
               // fails fast) — every other still-untried entry would likely
               // just time out too, one MUTATION_SYNC_TIMEOUT_MS at a time.
               // Stop this pass here instead of compounding that wait, and
               // requeue the rest exactly as they were. This still leaves
               // syncingRef/syncStateAtom properly reset below (unlike the
               // hang this replaces), so the next genuine reconnect — window's
               // 'online' event flipping isOnlineAtom back to true (see
               // useAuthBootstrap.ts) — re-runs this effect and picks up
               // where this pass left off, rather than being permanently
               // blocked by a sync that never finished.
               if (timedOut) {
                  failures.push(...remaining.slice(i + 1));
                  break;
               }
            }
         }

         // FIXED 2026-08-29 (Duane): "the daily report syncs
         // successfully, then the x of y and the row's status change
         // back to pending, like it wasn't completed." A queued mutation
         // succeeding here means the real DailyWellReport (or well/
         // RunTicket/BarrelTest) row now genuinely exists server-side —
         // confirmed live by reading it straight back from the backend
         // right after this fires — but nothing told any currently-
         // mounted query about it. WellsListPage's own
         // `reportWindowByWellId` (and the analogous `downSince` overlay)
         // reads real data from `dailyWellReport_AllWhere`/
         // `user_GetMyWells` layered with a client-side overlay of
         // whatever's still in `offlineQueueAtom` — the overlay is
         // exactly why a queued-but-unsynced report shows as Reported
         // immediately. But the instant this succeeds and
         // `succeededEntityKeys` removes the entry from the queue (see
         // applySyncResultAtom), the overlay's contribution disappears —
         // so refetching the underlying query's real data has to be done
         // by then, or the well drops right back to Pending/Overdue.
         //
         // `refetchQueries({include: 'active'})` re-runs every
         // currently-mounted query with active watchers — this hook is
         // global (mounted once in App.tsx, not per-page), so it has no
         // way to know which specific queries a given queued mutation
         // might affect (DailyWellReport_AllWhere, User_GetMyWells,
         // RunTicket_AllWhere, BarrelTest_AllWhere, ... — MUTATION_REGISTRY
         // spans all of them), and enumerating every possibly-affected
         // query here would silently drift out of sync as new mutation
         // types are added. Only worth doing when something actually
         // succeeded — a pass that was only failures (or nothing to sync)
         // has nothing new for any query to pick up.
         //
         // UPDATED 2026-08-31 (Duane, "sometimes it does update and
         // sometimes it doesn't"): this refetch now runs BEFORE
         // applySyncResult (not after) — see refetchActiveQueriesWithRetry's
         // own doc comment above for why the ordering itself, plus a
         // retry, was the fix. `unmountedRef` is re-checked afterward
         // since this can now take a few extra seconds across retries.
         if (succeededEntityKeys.length > 0 && !unmountedRef.current) {
            await refetchActiveQueriesWithRetry(client);
         }

         if (!unmountedRef.current) {
            await applySyncResult({succeededEntityKeys, failures});
            setSyncState(failures.length > 0 ? 'error' : 'idle');
            // Best-effort — a RunTicket create that just flushed above may
            // now exist for a photo that's been sitting pending. Failures
            // here don't affect the mutation-queue's own sync state; they
            // just retry on the next reconnect/useOfflinePhotoSync pass.
            await syncAllPendingRunTicketPhotos().catch(() => {});
         }
         syncingRef.current = false;
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isAuthed, isOnline, hydrated, pollTick]);
}
